/* eslint-disable indent */
/* eslint-disable max-len */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ShippingAddressFormComponent } from '@cart/components/checkout-page/checkout-personal-info/shipping-address-form/shipping-address-form.component';
import { CountryName } from '@core/constants/country-name';
import { ValidEmailRegExp } from '@core/constants/patterns';
import { phErrorMessages } from '@core/constants/ph-error-messages';
import { CartRoutes } from '@core/constants/routes.const';
import { ZipCodeMinimumLength } from '@core/constants/zip-code-minimum-length';
import { FormErrorTypes } from '@core/enums/form-error-type.enum';
import { PhExceptionErrorType } from '@core/enums/ph-exception-error-type.enum';
import { CheckoutAddress, CheckoutUserModel } from '@core/models/checkout.model';
import { GtmService } from '@core/services/gtm.service';
import { LogRocketService } from '@core/services/logrocket.service';
import { AppState } from '@core/store';
import { selectAddressIsFetched, selectAddresses, selectCountryStates } from '@core/store/address';
import { Address, AddressVerification } from '@core/store/address/address-state-models';
import { fetchAddresses, fetchCountryStates } from '@core/store/address/address.actions';
import { selectIsAuthenticated } from '@core/store/auth';
import { selectCart } from '@core/store/cart';
import {
  selectAddressErrorType,
  selectAddressVerification,
  selectCurrentAddress,
  selectEmailCheckErrorType,
  selectEmailChecked,
  selectIsRegisteredUserWithoutLogin,
  selectPersonalInfo,
  selectSaveSuccessful,
  selectUpdatePersonalInfoIsLoading,
  selectUserPrincipalCheckErrorType,
  selectUserPrincipalChecked,
} from '@core/store/checkout';
import { PersonalInfo } from '@core/store/checkout/checkout-state-models';
import {
  cancelPersonalInfoUpdate,
  checkUserByEmail,
  checkUserByUserPrincipal,
  openSavedAddressesModal,
  resetCheckoutAddressValidationModal,
  resetEmailCheck,
  resetLoginModal,
  resetSaveSuccessful,
  resetSavedAddressesModal,
  resetresetkUserByPrincipalNameCheck,
  updatePersonalInfo,
} from '@core/store/checkout/checkout.actions';
import {
  selectAssignedConsultant,
  selectCurrentConsultant,
  selectIsOutdatedData,
} from '@core/store/consultant';
import {
  fetchAssignedConsultantOnCheckout,
  setIsOutdatedData,
} from '@core/store/consultant/consultant.actions';
import { selectUser, selectUserIsFetched } from '@core/store/user';
import { UserModel } from '@core/store/user/user-state-models';
import { fetchUser } from '@core/store/user/user.actions';
import { setVoucherEmail } from '@core/store/voucher/voucher.actions';
import { environment } from '@env';
import { concatLatestFrom } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { SelectOption } from '@shared/components/select/select.component';
import { TextFieldInputComponent } from '@shared/components/text-field-input/text-field-input.component';
import { countryStatesToSelectOptions } from '@shared/utils/address-utils';
import { emailValidator } from '@shared/utils/email-validator-utils';
import { isMexEnv } from '@shared/utils/environment-utils';
import { scrollToTop } from '@shared/utils/scroll-utils';
import { namePatternValidator } from '@shared/validators/name-pattern.validator';
import { Observable, Subscription, combineLatest, fromEvent, merge } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  withLatestFrom,
} from 'rxjs/operators';

@Component({
  selector: 'app-checkout-personal-info',
  templateUrl: './checkout-personal-info.component.html',
  styleUrls: ['../checkout-slide.shared.scss', './checkout-personal-info.component.scss'],
})
export class CheckoutPersonalInfoComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output()
  clickNext: EventEmitter<number> = new EventEmitter<number>();

  @Input() title: string;

  @ViewChild('address', { static: true })
  shippingAddressForm: ShippingAddressFormComponent;

  @ViewChild('emailInput') emailInput: TextFieldInputComponent;
  @ViewChild('emailConfirmInput') emailConfirmInput: TextFieldInputComponent;
  @ViewChild('phoneNumber') phoneNumberInput: TextFieldInputComponent;
  @ViewChild('loginModal') loginModal: any;

  readonly PhErrorTypes = PhExceptionErrorType;
  readonly BillingCustomer = 'billingCustomer';
  readonly FirstName = 'firstName';
  readonly LastName = 'lastName';
  readonly Email = 'email';
  readonly EmailConfirm = 'emailConfirm';
  readonly PhoneNumber = 'phoneNumber';
  readonly Updates = 'updates';
  readonly ShippingAddress = 'shippingAddress';
  readonly Address = 'address';
  readonly FormErrorTypes = FormErrorTypes;
  readonly ErrorMessages = phErrorMessages;
  readonly ShippingCartRoute = CartRoutes.ShoppingCart;
  readonly contactMethod = 'contactMethod';
  readonly LoginViaPhone = CartRoutes.CreateAccount;

  personalInfoFormGroup: FormGroup;
  shippingAddressVerification$: Observable<AddressVerification>;
  countryStates$: Observable<SelectOption[]>;
  isAuthenticated$: Observable<boolean>;
  isNewsLetterSubscribe$: Observable<boolean>;
  userAddresses$: Observable<Address[]>;
  currentAddress$: Observable<Address>;
  disableButton$: Observable<boolean>;
  emailErrorType$: Observable<PhExceptionErrorType>;
  userPrincipalErrorType$: Observable<PhExceptionErrorType>;
  addressErrorType$: Observable<PhExceptionErrorType>;
  isRegisteredWithoutLogin$: Observable<boolean>;
  showPhoneRequiredMessage: boolean = false;
  showEmailRequiredMessage: boolean = false;
  showEmailConfirmRequiredMessage: boolean = false;
  showLoginModal: boolean = false;
  nextPageId: number;
  loginEmail: string;
  hideBillingAddressForm: boolean = true;
  showNameCheckbox: boolean = true;
  isEmailAddressAdded: boolean = false;
  selectedContactMethod: string = 'phone';
  isPhoneNumberAdded: boolean = true;
  bothContactMethodAdded: boolean = false;

  readonly ContactKeys = {
    isPhoneContact: 'contactByPhone',
    isEmailContact: 'contactByEmail',
  };

  private addressDefault: CheckoutAddress = {
    firstName: ['', [Validators.required,namePatternValidator()]],
    lastName: ['', [Validators.required,namePatternValidator()]],
    phoneNumber: ['', [Validators.required]],
    address: {
      id: [''],
      addressLine1: ['', [Validators.required]],
      addressLine2: [''],
      city: ['', [Validators.required, Validators.pattern(/^[\p{Letter}\p{Mark}\s-]+$/u)]],
      country: [CountryName[environment.country]],
      state: ['', [Validators.required]],
      zipCode: ['', [Validators.required, Validators.minLength(ZipCodeMinimumLength)]],
    },
  };

  private userDefault: CheckoutUserModel = {
    firstName: ['',
     [Validators.required,,
      namePatternValidator()]],
    lastName: ['',
     [Validators.required,
      namePatternValidator()]],
    email: ['', [Validators.required, emailValidator(ValidEmailRegExp)]],
    emailConfirm: ['', [Validators.required]],
    phoneNumber: ['', [Validators.required]],
    updates: [false],
    contactMethod: ['phone', [Validators.required]],
  };

  private subscriptions: Subscription = new Subscription();
  private verifiedShippingAddress: boolean;
  private isFormPrefilled: boolean = false;
  private emailValidationInitialized: boolean = false;
  private phoneValidationInitialized: boolean = false;
  private lastCheckedEmail: string = '';

  constructor(
    private store$: Store<AppState>,
    private fb: FormBuilder,
    private gtmService: GtmService,
    private logrocketService: LogRocketService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.setShippingAddressVerification();
    if (isMexEnv) this.addressDefault.address.addressLine3 = [''];

    this.store$.dispatch(resetSaveSuccessful());
    this.store$.dispatch(fetchCountryStates());
    this.countryStates$ = countryStatesToSelectOptions(this.store$.select(selectCountryStates));
    this.isAuthenticated$ = this.store$.select(selectIsAuthenticated);
    this.isNewsLetterSubscribe$ = this.store$.select(selectUser).pipe(select((u) => u.newsletters));
    this.personalInfoFormGroup = this.createFormGroup();
    this.isRegisteredWithoutLogin$ = this.store$.select(selectIsRegisteredUserWithoutLogin);

    const formKick$ = merge(
      this.personalInfoFormGroup.valueChanges,
      this.personalInfoFormGroup.statusChanges,
    ).pipe(startWith(null));

    this.setupContactMethodLocking();
    this.emailErrorType$ = this.store$.select(selectEmailCheckErrorType);
    this.userPrincipalErrorType$ = this.store$.select(selectUserPrincipalCheckErrorType);
    this.addressErrorType$ = this.store$.select(selectAddressErrorType);
    const formChanges$ = merge(
      this.personalInfoFormGroup.valueChanges,
      this.personalInfoFormGroup.statusChanges,
    ).pipe(startWith(null));
    this.disableButton$ = combineLatest([
      this.store$.select(selectUpdatePersonalInfoIsLoading),
      this.emailErrorType$,
      this.userPrincipalErrorType$,
      this.isAuthenticated$,
      this.isRegisteredWithoutLogin$,
      formChanges$,
      formKick$,
    ]).pipe(
      map(([loading, emailError, userPrincipalError, isAuth, isRegisteredWithoutLogin]) => {
        const emailAdded = this.selectedContactMethod === 'email' || this.isEmailAddressAdded;
        const phoneAdded = this.selectedContactMethod === 'phone' || this.isPhoneNumberAdded;

        const bc = this.personalInfoFormGroup.get(this.BillingCustomer);
        const emailControl = bc?.get(this.Email);
        const emailConfirmControl = bc?.get(this.EmailConfirm);
        const phoneControl = bc?.get('phoneNumber');

        const emailValue = emailControl?.value;
        const emailConfirmValue = emailConfirmControl?.value;
        const phoneValue = phoneControl?.value;
        const skipDupAndChecked = Boolean(isAuth || isRegisteredWithoutLogin);

        // Email validation
        let emailBlocked = false;
        if (emailAdded) {
          const emailEmpty = !emailValue || emailValue.trim() === '';
          const emailInvalid = emailControl?.invalid;
          const emailHasServerError = skipDupAndChecked
            ? false
            : emailError === this.PhErrorTypes.Email;
          const emailConfirmMismatch = skipDupAndChecked
            ? false
            : emailConfirmValue !== emailValue || !emailConfirmValue;

          emailBlocked = emailEmpty || emailInvalid || emailHasServerError || emailConfirmMismatch;
        }

        // Phone validation
        let phoneBlocked = false;
        if (phoneAdded) {
          const phoneEmpty = !phoneValue || phoneValue.trim() === '';
          const phoneInvalid = phoneControl?.invalid;

          const phoneHasServerError = skipDupAndChecked
            ? false
            : userPrincipalError === this.PhErrorTypes.UserPrincipal;

          phoneBlocked = phoneEmpty || phoneInvalid || phoneHasServerError;
        }

        const nothingSelected = !emailAdded && !phoneAdded;

        const isBillingInvalid = false;
        return (
          Boolean(loading) ||
          isBillingInvalid ||
          emailBlocked ||
          phoneBlocked ||
          (!this.bothContactMethodAdded && nothingSelected)
        );
      }),
    );

    const bc = this.personalInfoFormGroup.get(this.BillingCustomer) as FormGroup;
    const phoneCtrl = bc?.get(this.PhoneNumber);
    this.subscriptions.add(
      combineLatest([
        this.isAuthenticated$,
        this.isRegisteredWithoutLogin$,
        this.contactMethodCtrl!.valueChanges.pipe(startWith(this.contactMethodCtrl!.value)),
        phoneCtrl!.valueChanges.pipe(startWith(phoneCtrl!.value)),
        this.store$.select(selectUserPrincipalChecked),
        this.userPrincipalErrorType$,
      ]).subscribe(
        ([isAuth, isRegisteredWithoutLogin, method, phoneVal, , phoneError]: [
          boolean,
          boolean,
          any,
          any,
          boolean,
          PhExceptionErrorType | any,
        ]) => {
          if (
            !isAuth &&
            !isRegisteredWithoutLogin &&
            method === 'phone' &&
            !this.bothContactMethodAdded
          ) {
            const phoneEmpty = !phoneVal || phoneVal.toString().trim() === '';
            if (phoneEmpty) {
              this.showPhoneRequiredMessage = true;
            } else {
              const hasError = phoneError === this.PhErrorTypes.UserPrincipal;
              this.showPhoneRequiredMessage = hasError;
            }
          } else {
            this.showPhoneRequiredMessage = false;
          }
        },
      ),
    );

    const emailCtrl = bc?.get(this.Email);
    if (emailCtrl) {
      this.subscriptions.add(
        combineLatest([
          this.isAuthenticated$,
          this.isRegisteredWithoutLogin$,
          this.contactMethodCtrl!.valueChanges.pipe(startWith(this.contactMethodCtrl!.value)),
          emailCtrl.valueChanges.pipe(startWith(emailCtrl.value)),
        ]).subscribe(
          ([isAuth, isRegisteredWithoutLogin, method, emailVal]: [boolean, boolean, any, any]) => {
            if (
              !isAuth &&
              !isRegisteredWithoutLogin &&
              method === 'email' &&
              !this.bothContactMethodAdded
            ) {
              const emailEmpty = !emailVal || emailVal.toString().trim() === '';
              this.showEmailRequiredMessage = emailEmpty;
            } else {
              this.showEmailRequiredMessage = false;
            }
          },
        ),
      );
    }

    const emailConfirmCtrl = bc?.get(this.EmailConfirm);
    if (emailConfirmCtrl) {
      this.subscriptions.add(
        combineLatest([
          this.isAuthenticated$,
          this.isRegisteredWithoutLogin$,
          this.contactMethodCtrl!.valueChanges.pipe(startWith(this.contactMethodCtrl!.value)),
          emailConfirmCtrl.valueChanges.pipe(startWith(emailConfirmCtrl.value)),
        ]).subscribe(
          ([isAuth, isRegisteredWithoutLogin, method, emailConfirmVal]: [
            boolean,
            boolean,
            any,
            any,
          ]) => {
            if (
              !isAuth &&
              !isRegisteredWithoutLogin &&
              method === 'email' &&
              !this.bothContactMethodAdded
            ) {
              const confirmEmpty = !emailConfirmVal || emailConfirmVal.toString().trim() === '';
              this.showEmailConfirmRequiredMessage = confirmEmpty;
            } else {
              this.showEmailConfirmRequiredMessage = false;
            }
          },
        ),
      );
    }

    this.currentAddress$ = this.store$.select(selectCurrentAddress);
    this.subscribeAddressValueChanges();
    this.subscribeUserAuthenticated();
    this.subscribePrepopulateForm();
    this.subscribeSaveSuccessful();
    this.resetEmailCheck();
    this.addressVerificationErrorHandling();
    this.resetUserPrincipalCheck();
    this.gtmCheckout();

    this.emailphoneValidtion('phone');

    const cm = this.contactMethodCtrl;
    if (cm) {
      this.subscriptions.add(
        cm.valueChanges
          .pipe(startWith(cm.value as 'phone' | 'email'), distinctUntilChanged())
          .subscribe((method: 'phone' | 'email') => {
            if (this.bothContactMethodAdded) {
              this.selectedContactMethod = method;
              return;
            }

            this.bothContactMethodAdded = false;
            this.selectedContactMethod = method;

            if (method === 'email') {
              this.isEmailAddressAdded = true;
              this.isPhoneNumberAdded = false;

              this.emailphoneValidtion('email');

              const phoneControl = bc.get(this.PhoneNumber);
              if (phoneControl) {
                this.clearControl(phoneControl);
                this.store$.dispatch(resetresetkUserByPrincipalNameCheck());
                if (this.phoneNumberInput && this.phoneNumberInput.input) {
                  this.phoneNumberInput.input.nativeElement.value = '';
                }
              }

              setTimeout(() => this.validateEmail(), 100);
            } else {
              this.isPhoneNumberAdded = true;
              this.isEmailAddressAdded = false;

              this.emailphoneValidtion('phone');
              this.resetEmailCheck();

              const emailControl = bc.get(this.Email);
              const emailConfirmControl = bc.get(this.EmailConfirm);
              if (emailControl) {
                this.clearControl(emailControl);
              }
              if (emailConfirmControl) {
                this.clearControl(emailConfirmControl);
              }
              if (this.emailInput && this.emailInput.input) {
                this.emailInput.input.nativeElement.value = '';
              }
              if (this.emailConfirmInput && this.emailConfirmInput.input) {
                this.emailConfirmInput.input.nativeElement.value = '';
              }

              setTimeout(() => this.onPhoneBlur(), 100);
            }
          }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  next(nextPageId: number): void {
    this.personalInfoFormGroup.markAllAsTouched();
    this.nextPageId = nextPageId - 1;
    this.logFormValidationErrors(this.personalInfoFormGroup);
    this.logFormValidationErrors(this.personalInfoFormGroup);
    if (this.personalInfoFormGroup.valid) {
      this.store$.dispatch(setIsOutdatedData({ isOutdatedData: true }));
      const personalInfo = this.personalInfoFormGroup.getRawValue() as PersonalInfo;

      this.logrocketService.logRocketIdentify(personalInfo.billingCustomer.email);
      this.store$.dispatch(
        updatePersonalInfo({ personalInfo, skipAddressVerification: this.verifiedShippingAddress }),
      );
      this.resetAddressVerificationError();
    }
  }

  logFormValidationErrors(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control && control.errors) {
        console.log(`Errors for ${key}:`, control.errors);
      }
    });
  }

  saveShippingAddressForm = (formGroup: FormGroup): void => {
    this.personalInfoFormGroup.get(this.ShippingAddress).patchValue({
      [this.Address]: {
        ...formGroup.value,
      },
    });
    const personalInfo: PersonalInfo = this.personalInfoFormGroup.value;

    this.store$.dispatch(
      updatePersonalInfo({
        personalInfo,
        skipAddressVerification: true,
      }),
    );
    this.store$.dispatch(resetCheckoutAddressValidationModal());
    this.verifiedShippingAddress = true;
  };

  onDismissShippingAddress = (): void => {
    this.store$.dispatch(resetCheckoutAddressValidationModal());
    this.store$.dispatch(cancelPersonalInfoUpdate());
  };

  dismissSavedAddressesModal = (): void => {
    this.store$.dispatch(resetSavedAddressesModal());
  };

  onPhoneBlur() {
    if (this.phoneValidationInitialized) {
      return;
    }
    this.phoneValidationInitialized = true;

    if (this.phoneNumberInput) {
      const phoneKeyup$ = fromEvent(this.phoneNumberInput.input.nativeElement, 'keyup').pipe(
        debounceTime(500),
      );
      const phoneBlur$ = fromEvent(this.phoneNumberInput.input.nativeElement, 'blur');

      this.subscriptions.add(
        merge(phoneKeyup$, phoneBlur$)
          .pipe(
            map(() => this.phoneNumberInput.value),
            distinctUntilChanged(),
            withLatestFrom(this.isAuthenticated$),
            filter(([, isAuth]) => !isAuth),
          )
          .subscribe(([phoneValue]) => {
            let phoneNumb = phoneValue;
            if (phoneNumb != null && phoneNumb !== '') {
              phoneNumb = phoneNumb.toString().replace(/(?!^\+)\D/g, '');
              if (
                (phoneNumb.startsWith('+52') || phoneNumb.startsWith('+1')) &&
                phoneNumb.length >= 11
              ) {
                this.store$.dispatch(
                  checkUserByUserPrincipal({
                    userPrincipal: phoneNumb,
                  }),
                );
              }
            }
          }),
      );
    }
  }

  onContactMethodClick(method: string) {
    if (this.contactMethodCtrl?.disabled) return;
    this.contactMethodCtrl?.setValue(method as 'phone' | 'email');
  }

  addContact() {
    this.subscriptions.add(
      this.isAuthenticated$.pipe(take(1)).subscribe((isAuth) => {
        if (isAuth) return;

        this.bothContactMethodAdded = true;
        this.isEmailAddressAdded = true;
        this.isPhoneNumberAdded = true;
        this.applyBothContactValidation();

        const bc = this.personalInfoFormGroup.get(this.BillingCustomer) as FormGroup;
        const emailControl = bc?.get(this.Email);
        const emailConfirmControl = bc?.get(this.EmailConfirm);
        const phoneControl = bc?.get('phoneNumber');
        bc?.get('phoneNumber')?.markAsTouched();
        bc?.get(this.Email)?.markAsTouched();
        bc?.get(this.EmailConfirm)?.markAsTouched();

        if (
          emailControl &&
          emailConfirmControl &&
          emailControl.valid &&
          emailConfirmControl.valid &&
          emailControl.value === emailConfirmControl.value &&
          emailControl.value
        ) {
          const currentEmail = emailControl.value;
          if (currentEmail !== this.lastCheckedEmail) {
            this.lastCheckedEmail = currentEmail;
            this.store$.dispatch(resetLoginModal());
            this.store$.dispatch(checkUserByEmail({ email: currentEmail }));
          }
        } else {
          setTimeout(() => this.validateEmail(), 100);
        }

        const phoneVal = phoneControl?.value;
        if (phoneVal) {
          let digits = phoneVal.toString().replace(/(?!^\+)\D/g, '');
          if ((digits.startsWith('+52') || digits.startsWith('+1')) && digits.length >= 11) {
            this.store$.dispatch(checkUserByUserPrincipal({ userPrincipal: digits }));
          }
        } else {
          setTimeout(() => this.onPhoneBlur(), 100);
        }
      }),
    );
  }

  validateEmail() {
    if (this.emailValidationInitialized) {
      return;
    }
    this.emailValidationInitialized = true;

    if (this.emailInput && this.emailConfirmInput) {
      const emailBlur$ = fromEvent(this.emailInput.input.nativeElement, 'blur').pipe(startWith({}));
      const emailKeyup$ = fromEvent(this.emailInput.input.nativeElement, 'keyup').pipe(
        debounceTime(500),
        startWith({}),
        distinctUntilChanged(),
      );
      const emailConfirmBlur$ = fromEvent(this.emailConfirmInput.input.nativeElement, 'blur').pipe(
        startWith({}),
      );
      const emailConfirmKeyup$ = fromEvent(
        this.emailConfirmInput.input.nativeElement,
        'keyup',
      ).pipe(debounceTime(500), startWith({}), distinctUntilChanged());

      this.subscriptions.add(
        combineLatest([emailBlur$, emailKeyup$, emailConfirmBlur$, emailConfirmKeyup$])
          .pipe(withLatestFrom(this.isAuthenticated$.pipe(filter((isAuth) => !isAuth))))
          .subscribe(() => {
            const emailField = this.personalInfoFormGroup.get(
              `${this.BillingCustomer}.${this.Email}`,
            );

            this.updateEmailConfirmValidity();

            if (
              emailField?.valid &&
              this.emailInput.value === this.emailConfirmInput.value &&
              this.emailInput.value !== ''
            ) {
              const currentEmail = this.emailInput.value;
              if (currentEmail !== this.lastCheckedEmail) {
                this.lastCheckedEmail = currentEmail;
                this.loginEmail = currentEmail;
                this.store$.dispatch(resetLoginModal());
                this.store$.dispatch(checkUserByEmail({ email: currentEmail }));
              }
            }
          }),
      );
    }
  }

  selectAddress(selectedAddressId: number): void {
    this.userAddresses$
      .pipe(
        take(1),
        map((addresses) => addresses.find((address) => address.id === selectedAddressId)),
      )
      .subscribe((selectedAddress) => {
        this.showNameCheckbox = !selectedAddress.firstName || !selectedAddress.lastName;
        this.personalInfoFormGroup.controls[this.ShippingAddress].patchValue({
          firstName: selectedAddress.firstName,
          lastName: selectedAddress.lastName,
          address: {
            id: selectedAddress.id,
            addressLine1: selectedAddress.addressLine1,
            addressLine2: selectedAddress.addressLine2,
            city: selectedAddress.city,
            state: selectedAddress.state,
            zipCode: selectedAddress.zipCode,
          },
        });
        if (isMexEnv) {
          this.personalInfoFormGroup.controls[this.ShippingAddress].patchValue({
            addressLine3: selectedAddress.addressLine3,
          });
        }
        this.verifiedShippingAddress = true;
      });
  }

  changeAddress(): void {
    this.store$.dispatch(
      openSavedAddressesModal({
        currentAddress: {
          ...this.personalInfoFormGroup.get(`${this.ShippingAddress}.${this.Address}`).value,
        },
      }),
    );
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();

    setTimeout(() => {
      this.personalInfoFormGroup.updateValueAndValidity({ emitEvent: true });

      const bc = this.personalInfoFormGroup.get(this.BillingCustomer) as FormGroup;
      if (bc) {
        const emailControl = bc.get(this.Email);
        const emailConfirmControl = bc.get(this.EmailConfirm);
        const phoneControl = bc.get(this.PhoneNumber);

        if (
          emailControl &&
          emailConfirmControl &&
          emailControl.value &&
          emailControl.value === emailConfirmControl.value
        ) {
          this.validateEmail();
        }

        if (phoneControl && phoneControl.value) {
          this.onPhoneBlur();
        }
      }
    }, 300);

    setTimeout(() => {
      if (this.phoneNumberInput) {
        this.onPhoneBlur();
      }
    }, 200);

    this.subscriptions.add(
      combineLatest([
        this.emailErrorType$,
        this.store$.select(selectEmailChecked),
        this.isAuthenticated$,
        this.isRegisteredWithoutLogin$,
      ]).subscribe(([emailError, emailChecked, isAuth, isRegisteredWithoutLogin]) => {
        if (!isAuth && !isRegisteredWithoutLogin) {
          if (emailChecked && emailError === this.PhErrorTypes.Email) {
            this.showLoginModal = true;
            setTimeout(() => {
              if (this.loginModal && typeof this.loginModal.openModal === 'function') {
                this.loginModal.openModal();
              }
            }, 0);
          } else {
            this.showLoginModal = false;
            this.store$.dispatch(resetLoginModal());
          }
        } else {
          this.showLoginModal = false;
          this.store$.dispatch(resetLoginModal());
        }
      }),
    );
  }

  toggleShippingName(usePersonalInfoName: boolean) {
    this.personalInfoFormGroup.get(`${this.ShippingAddress}`).patchValue({
      [this.FirstName]: usePersonalInfoName
        ? this.personalInfoFormGroup.get(`${this.BillingCustomer}.${this.FirstName}`).value
        : '',
      [this.LastName]: usePersonalInfoName
        ? this.personalInfoFormGroup.get(`${this.BillingCustomer}.${this.LastName}`).value
        : '',
    });
  }

  private clearControl(ctrl: AbstractControl) {
    ctrl.reset('', { emitEvent: true });

    ctrl.markAsPristine();
    ctrl.markAsUntouched();
    ctrl.setErrors(null);
    ctrl.updateValueAndValidity({ emitEvent: true });
  }

  private prepopulateForm(
    userIsFetched: boolean,
    user: UserModel,
    addressIsFetched: boolean,
    address: Address,
    personalInfoFromCheckout: PersonalInfo,
    isAuth: boolean,
  ): void {
    let personalInfo: PersonalInfo;
    if (!!personalInfoFromCheckout) {
      personalInfo = personalInfoFromCheckout;
    } else if (userIsFetched && addressIsFetched) {
      const email = this.normalizeStr((user as any)?.email);
      const phone = this.pickUserPhone(user);

      personalInfo = {
        billingCustomer: {
          ...user,
          email,
          emailConfirm: email,
          phoneNumber: phone,
          updates: (user as any)?.newsletters ?? false,
          contactMethod: user.contactMethod,
        },
        shippingAddress: {
          firstName: address?.firstName || '',
          lastName: address?.lastName || '',
          phoneNumber: '',
          address,
        },
      };
    }

    if (personalInfo && !this.isFormPrefilled) {
      this.personalInfoFormGroup.patchValue(personalInfo);

      this.showNameCheckbox =
        !personalInfo.shippingAddress.firstName || !personalInfo.shippingAddress.lastName;

      if (!isAuth) {
        const prepopEmail = personalInfo.billingCustomer.email;
        if (prepopEmail && prepopEmail !== this.lastCheckedEmail) {
          this.lastCheckedEmail = prepopEmail;
          this.store$.dispatch(resetLoginModal());
          this.store$.dispatch(checkUserByEmail({ email: prepopEmail }));
        }
      }

      this.verifiedShippingAddress = true;
      this.isFormPrefilled = true;
    }

    const bc = this.personalInfoFormGroup.get(this.BillingCustomer) as FormGroup;
    const contactMethodCtrl = bc.get(this.contactMethod);
    const phoneCtrl = bc.get(this.PhoneNumber);
    const emailCtrl = bc.get(this.Email);

    if (isAuth) {
      contactMethodCtrl?.disable({ emitEvent: false });
      if (user?.email) {
        emailCtrl?.disable({ emitEvent: false });
      }
      if (user?.phoneNumber) {
        phoneCtrl?.disable({ emitEvent: false });
      }
    } else {
      contactMethodCtrl?.enable({ emitEvent: false });
      phoneCtrl?.enable({ emitEvent: false });
      emailCtrl?.enable({ emitEvent: false });
    }
  }

  private normalizeStr(v: any): string {
    return (v ?? '').toString().trim();
  }

  private pickUserPhone(user: any): string {
    return this.normalizeStr(
      user?.phoneNumber ?? user?.mobilePhone ?? user?.cellPhone ?? user?.phone ?? '',
    );
  }

  private setupContactMethodLocking(): void {
    const bc = this.personalInfoFormGroup.get('billingCustomer') as FormGroup;

    const contactMethodCtrl = bc.get('contactMethod');
    const phoneCtrl = bc.get('phoneNumber');
    const emailCtrl = bc.get('email');

    if (!contactMethodCtrl || !phoneCtrl || !emailCtrl) return;

    this.subscriptions.add(
      combineLatest([
        this.isAuthenticated$,
        contactMethodCtrl.valueChanges.pipe(startWith(contactMethodCtrl.value)),
        phoneCtrl.valueChanges.pipe(startWith(phoneCtrl.value)),
        emailCtrl.valueChanges.pipe(startWith(emailCtrl.value)),
      ])
        .pipe(
          map(([isAuth, method, phone, email]) => {
            if (!isAuth) return false;

            const phoneCommitted =
              (phoneCtrl.dirty || phoneCtrl.touched) && !!(phone ?? '').toString().trim();

            const emailCommitted =
              (emailCtrl.dirty || emailCtrl.touched) && !!(email ?? '').toString().trim();

            return method === 'phone' ? phoneCommitted : emailCommitted;
          }),
          distinctUntilChanged(),
          filter(Boolean),
          take(1),
        )
        .subscribe(() => {
          contactMethodCtrl.disable({ emitEvent: false });
        }),
    );
  }

  private updateEmailConfirmValidity(): void {
    const billingCustomerGroup = this.personalInfoFormGroup.get(this.BillingCustomer) as FormGroup;
    const emailControl = billingCustomerGroup?.get(this.Email);
    const emailConfirmControl = billingCustomerGroup?.get(this.EmailConfirm);

    if (!emailControl || !emailConfirmControl || !billingCustomerGroup) {
      return;
    }

    const emailValue = emailControl.value?.trim() || '';
    const emailConfirmValue = emailConfirmControl.value?.trim() || '';
    const currentGroupErrors = billingCustomerGroup.errors || {};

    if (emailValue !== emailConfirmValue && emailConfirmValue !== '') {
      // Set error on the GROUP level
      billingCustomerGroup.setErrors({
        ...currentGroupErrors,
        [this.FormErrorTypes.emailConfirm]: true,
      });
    } else {
      // Clear the email confirm error from the group
      if (currentGroupErrors[this.FormErrorTypes.emailConfirm]) {
        delete currentGroupErrors[this.FormErrorTypes.emailConfirm];
        const remainingErrors =
          Object.keys(currentGroupErrors).length > 0 ? currentGroupErrors : null;
        billingCustomerGroup.setErrors(remainingErrors);
      }
    }
  }

  private subscribeAddressValueChanges(): void {
    this.subscriptions.add(
      this.personalInfoFormGroup
        .get(`${this.ShippingAddress}.${this.Address}`)
        .valueChanges.subscribe(() => (this.verifiedShippingAddress = false)),
    );
  }

  private setShippingAddressVerification(): void {
    this.shippingAddressVerification$ = this.store$
      .select(selectAddressVerification)
      .pipe(select((data) => data?.shippingAddressVerificationResult));
  }

  private addressVerificationErrorHandling(): void {
    this.subscriptions.add(
      this.shippingAddressVerification$.subscribe((verification) => {
        if (!!verification && !verification?.correctedAddress && verification?.isValid === false) {
          const shippingAddressForm = this.personalInfoFormGroup?.get(
            `${this.ShippingAddress}.${this.Address}`,
          ) as FormGroup;
          Object.keys(shippingAddressForm.controls).forEach((key) => {
            shippingAddressForm.get(key).setErrors({ uniqueness: true });
          });
        }
      }),
    );
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      [this.BillingCustomer]: this.fb.group(this.userDefault),
      [this.ShippingAddress]: this.shippingAddressForm.createGroup(this.addressDefault),
    });
  }

  get contactMethodCtrl() {
    return this.personalInfoFormGroup.get(`${this.BillingCustomer}.contactMethod`);
  }

  private emailphoneValidtion(validationPref: string) {
    const bc = this.personalInfoFormGroup.get(this.BillingCustomer);
    const emailControl = bc?.get(this.Email);
    const phoneControl = bc?.get('phoneNumber');
    const emailConfirmControl = bc?.get(this.EmailConfirm);

    if (validationPref === 'phone') {
      emailControl?.clearValidators();
      emailConfirmControl?.clearValidators();

      phoneControl?.setValidators([Validators.required]);
    } else if (validationPref === 'email') {
      phoneControl?.clearValidators();

      emailControl?.setValidators([Validators.required, Validators.email]);

      if (emailConfirmControl) {
        emailConfirmControl?.setValidators([Validators.required, Validators.email]);
      }
    }

    emailControl?.updateValueAndValidity({ emitEvent: false });
    phoneControl?.updateValueAndValidity({ emitEvent: false });
    emailConfirmControl?.updateValueAndValidity({ emitEvent: false });
  }

  private applyBothContactValidation() {
    const bc = this.personalInfoFormGroup.get(this.BillingCustomer);
    const emailControl = bc?.get(this.Email);
    const phoneControl = bc?.get('phoneNumber');
    const emailConfirmControl = bc?.get(this.EmailConfirm);

    phoneControl?.setValidators([Validators.required]);
    emailControl?.setValidators([Validators.required, Validators.email]);
    if (emailConfirmControl) {
      emailConfirmControl?.setValidators([Validators.required, Validators.email]);
    }

    emailControl?.updateValueAndValidity({ emitEvent: false });
    phoneControl?.updateValueAndValidity({ emitEvent: false });
    emailConfirmControl?.updateValueAndValidity({ emitEvent: false });
  }

  private subscribeUserAuthenticated(): void {
    this.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        take(1),
      )
      .subscribe(() => {
        this.store$.dispatch(fetchAddresses());
        this.store$.dispatch(fetchUser());
        this.userAddresses$ = this.store$.select(selectAddresses);
      });
  }

  private subscribePrepopulateForm(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.select(selectUser),
        this.store$
          .select(selectAddresses)
          .pipe(map((addresses) => addresses.find((address) => address.default))),
        this.store$.select(selectPersonalInfo),
      ])
        .pipe(
          concatLatestFrom(() => [
            this.isAuthenticated$,
            this.store$.select(selectUserIsFetched),
            this.store$.select(selectAddressIsFetched),
          ]),
          filter(([[, , personalInfo], isAuth]) => isAuth || !!personalInfo),
        )
        .subscribe(([[user, address, personalInfo], isAuth, userIsFetched, addressIsFetched]) => {
          this.prepopulateForm(
            userIsFetched,
            user,
            addressIsFetched,
            address,
            personalInfo,
            isAuth,
          );
        }),
    );
  }

  private subscribeSaveSuccessful(): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.select(selectSaveSuccessful),
        this.store$.select(selectIsOutdatedData),
      ])
        .pipe(
          filter(([saveSuccessful, isOutdatedData]) => saveSuccessful && !isOutdatedData),
          concatLatestFrom(() => [
            this.store$.select(selectCurrentConsultant),
            this.store$.select(selectAssignedConsultant),
            this.store$.select(selectIsAuthenticated),
          ]),
        )
        .subscribe(([, currentConsultant, assignedConsultant, isAuthenticated]) => {
          if (isAuthenticated && !currentConsultant && assignedConsultant) {
            this.store$.dispatch(fetchAssignedConsultantOnCheckout());
          } else {
            if (isMexEnv) {
              const email = this.personalInfoFormGroup
                .get(this.BillingCustomer)
                .get(this.Email).value;
              this.store$.dispatch(setVoucherEmail({ email }));
            }
            this.clickNext.emit(this.nextPageId);
            scrollToTop();
          }
        }),
    );
  }

  private gtmCheckout() {
    this.store$
      .select(selectCart)
      .pipe(take(1))
      .subscribe((cart) => this.gtmService.beginCheckout(cart));
  }

  private resetEmailCheck(): void {
    const emailField = this.personalInfoFormGroup.get(`${this.BillingCustomer}.${this.Email}`);
    const emailConfirmField = this.personalInfoFormGroup.get(
      `${this.BillingCustomer}.${this.EmailConfirm}`,
    );
    this.subscriptions.add(
      combineLatest([emailField.valueChanges, emailConfirmField.valueChanges]).subscribe(() => {
        this.store$.dispatch(resetEmailCheck());
        this.store$.dispatch(resetLoginModal());
      }),
    );
  }

  private resetUserPrincipalCheck(): void {
    const phoneNumberField = this.personalInfoFormGroup.get(
      `${this.BillingCustomer}.${this.PhoneNumber}`,
    );

    this.subscriptions.add(
      phoneNumberField.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => this.store$.dispatch(resetresetkUserByPrincipalNameCheck())),
    );
  }

  private resetAddressVerificationError(): void {
    this.subscriptions.add(
      this.personalInfoFormGroup
        .get(`${this.ShippingAddress}.${this.Address}`)
        .valueChanges.pipe(take(1))
        .subscribe(() => {
          const addressForm = this.personalInfoFormGroup.get(
            `${this.ShippingAddress}.${this.Address}`,
          ) as FormGroup;
          Object.keys(addressForm.controls).forEach((key) =>
            addressForm.get(key).updateValueAndValidity(),
          );
        }),
    );
  }
}
